import React, { createContext, useContext, useState, useEffect } from "react";
import socket from "../../socket";

const NotificationContext = createContext();

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch notifications from API - WITH 5 SECOND TIMEOUT
  const fetchNotifications = async () => {
    try {
      console.log("📬 Fetching notifications...");
      setIsLoading(true);

      // Create AbortController with 5 second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.warn("⏱️ Notifications fetch timeout - using empty list");
        controller.abort();
      }, 5000);

      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/admin/notifications", {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await res.json();

      console.log("✅ Notifications fetched:", data.length ? `${data.length} items` : "empty");

      // Handle both array and object responses
      if (Array.isArray(data)) {
        setNotifications(data);
        const unread = data.filter((n) => !n.is_read).length;
        setUnreadCount(unread);
        console.log("✅ Set notifications:", data.length, "items, unread:", unread);
      } else if (data && typeof data === "object") {
        console.warn("⚠️ Notifications API returned object, expected array");
        setNotifications([]);
        setUnreadCount(0);
      } else {
        console.warn("⚠️ Notifications API returned unexpected format:", typeof data);
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.warn("⏱️ Notifications fetch timed out - setting empty list");
      } else {
        console.error("❌ Failed to fetch notifications:", err);
      }
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ EXPORT this function so NotificationBell can call it
  const refreshNotifications = fetchNotifications;

  // ✅ FIXED: Only fetch once on mount, then listen to socket events
  useEffect(() => {
    // Fetch once on mount with small delay
    const timer = setTimeout(() => {
      fetchNotifications();
    }, 500);

    // ✅ REMOVED setInterval - listen to socket instead
    const handleNewNotification = (data) => {
      console.log("📢 Real-time notification received via socket:", data);
      fetchNotifications(); // Refetch when new notification arrives
    };

    socket.on("new-deposit-notification", handleNewNotification);

    return () => {
      clearTimeout(timer);
      socket.off("new-deposit-notification", handleNewNotification);
    };
  }, []);

  // Mark notification as read
  const markAsRead = async (notification_id) => {
    try {
      await fetch("https://dshb-deposits.its.uiowa.edu/api/notifications/mark-read", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notification_id }),
      });

      setNotifications((prev) =>
        prev.map((n) =>
          n.notification_id === notification_id ? { ...n, is_read: true } : n
        )
      );

      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("❌ Failed to mark as read:", err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      console.log("📌 Marking all notifications as read...");
      
      await fetch("https://dshb-deposits.its.uiowa.edu/api/notifications/mark-all-read", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      // ✅ Update local state only - don't refetch
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
      
      console.log("✅ All marked as read");
    } catch (err) {
      console.error("❌ Failed to mark all as read:", err);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        setNotifications,
        unreadCount,
        isLoading,
        fetchNotifications,
        refreshNotifications,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
};